<script setup>
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  value: {
    type: [String, Number],
    required: true,
  },
  icon: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: 'slate',
    validator: value =>
      [
        'slate',
        'blue',
        'teal',
        'amber',
        'ruby',
        'green',
        'yellow',
        'red',
      ].includes(value),
  },
  // Indicador de tendência opcional (ex.: "18%" vs. o período anterior).
  // Ausente por padrão — não afeta os consumidores existentes (lead-score,
  // pipeline sequences) que não passam essas props.
  // `trendDirection` é o sinal MATEMÁTICO do número (subiu/desceu) — decide a
  // seta. `trendPositive` é o julgamento SEMÂNTICO (bom/ruim) — decide a cor.
  // São independentes: pra tempo de resposta, o valor DESCER é bom, então a
  // seta aponta pra baixo (reflete o número) e a cor fica verde (reflete que
  // é bom) ao mesmo tempo — misturar os dois numa boolean só fazia a seta
  // apontar "pra cima" (ícone de sucesso) quando o tempo na verdade caiu.
  trendLabel: {
    type: String,
    default: '',
  },
  trendDirection: {
    type: String,
    default: 'up',
    validator: value => ['up', 'down'].includes(value),
  },
  trendPositive: {
    type: Boolean,
    default: true,
  },
});

const colorClasses = {
  slate: 'bg-n-alpha-2 text-n-slate-11',
  blue: 'bg-n-blue-3 text-n-blue-11',
  teal: 'bg-n-teal-3 text-n-teal-11',
  green: 'bg-n-teal-3 text-n-teal-11',
  amber: 'bg-n-amber-3 text-n-amber-11',
  yellow: 'bg-n-amber-3 text-n-amber-11',
  ruby: 'bg-n-ruby-3 text-n-ruby-11',
  red: 'bg-n-ruby-3 text-n-ruby-11',
};

const iconColorClass = colorClasses[props.color] || colorClasses.slate;
</script>

<template>
  <!-- Em telas estreitas o cartão fica COMPACTO: ícone menor e em cima, padding
       menor, rótulo quebrando em vez de truncar. Isso é o que torna duas colunas
       viáveis no celular — em linha, o ícone (48px) mais o padding (40px) comem
       104px de um cartão de ~155px e sobram ~50px para um rótulo que precisa de
       77-105px, que foi exatamente como três cartões distintos passaram a ler
       "Lead...". A partir de `sm` volta o formato em linha de sempre.
       O ícone é DEIXA, não protagonista: `size-10` no lugar de `size-12` no
       desktop devolve a hierarquia ao número, que é o que se vem ler aqui. -->
  <div
    class="flex flex-col items-start gap-2 rounded-2xl bg-n-solid-2 p-4 shadow outline-1 outline outline-n-container sm:flex-row sm:items-center sm:gap-4 sm:p-5"
  >
    <div
      v-if="icon"
      class="flex size-8 items-center justify-center rounded-lg sm:size-10 sm:rounded-xl"
      :class="[iconColorClass]"
    >
      <Icon :icon="icon" class="size-4 sm:size-5" />
    </div>
    <div class="min-w-0 flex-1">
      <p class="break-words text-sm text-n-slate-11 sm:truncate">
        {{ title }}
      </p>
      <p class="text-2xl font-semibold text-n-slate-12 tabular-nums">
        {{ value }}
      </p>
      <p
        v-if="trendLabel"
        class="mt-1 flex items-center gap-1 text-xs font-medium tabular-nums"
        :class="trendPositive ? 'text-n-teal-10' : 'text-n-ruby-9'"
      >
        <Icon
          :icon="
            trendDirection === 'up'
              ? 'i-lucide-trending-up'
              : 'i-lucide-trending-down'
          "
          class="size-3"
        />
        {{ trendLabel }}
      </p>
    </div>
  </div>
</template>
